import { Box, ButtonBase, Link as MuiLink, Stack, Typography } from "@mui/material";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { CersDecisionStatus, CersModule } from "generated-graphql/graphql";
import { Link as RouterLink } from "react-router-dom";

import { BigTooltip } from "./BigTooltip";
import { StatusChip, StatusChipColor, StatusChipVariant } from "./StatusChip";

export type CersDecisionSummaryCellSummary = {
  moduleDecisions: ReadonlyArray<{
    module: CersModule;
    status: CersDecisionStatus;
    decisionDate?: string | null;
    revisionReportId?: string | null;
  }>;
};

type Props = {
  summary: CersDecisionSummaryCellSummary | null | undefined;
  variant: "customer" | "staff";
  state: string | null | undefined;
  tenantId?: string;
  facilityName?: string;
};

const IN_REVIEW_STATUSES: ReadonlyArray<CersDecisionStatus> = [
  CersDecisionStatus.Submitted,
  CersDecisionStatus.UnderReview,
];

const ACCEPTED_STATUSES: ReadonlyArray<CersDecisionStatus> = [
  CersDecisionStatus.Accepted,
  CersDecisionStatus.Certified,
];

function formatUtcDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { timeZone: "UTC" });
}

type DecisionGroup = {
  key: string;
  label: string;
  color: StatusChipColor;
  chipVariant?: StatusChipVariant;
  decisions: CersDecisionSummaryCellSummary["moduleDecisions"];
};

// Group order matches the cell's chip order (accepted, not accepted, in
// review, N/A) so the popover decodes the chips one-for-one.
function groupDecisions(
  decisions: CersDecisionSummaryCellSummary["moduleDecisions"]
): DecisionGroup[] {
  const isAccepted = (s: CersDecisionStatus) => ACCEPTED_STATUSES.includes(s);
  const isInReview = (s: CersDecisionStatus) => IN_REVIEW_STATUSES.includes(s);
  const groups: DecisionGroup[] = [
    {
      key: "accepted",
      label: "Accepted",
      color: "success",
      decisions: decisions.filter((d) => isAccepted(d.status)),
    },
    {
      key: "not-accepted",
      label: "Not Accepted",
      color: "error",
      decisions: decisions.filter((d) => d.status === CersDecisionStatus.NotAccepted),
    },
    {
      key: "in-review",
      label: "In Review",
      color: "info",
      decisions: decisions.filter((d) => isInReview(d.status)),
    },
    {
      key: "not-applicable",
      label: "Not Applicable",
      color: "default",
      chipVariant: "outlined",
      decisions: decisions.filter(
        (d) =>
          !isAccepted(d.status) &&
          d.status !== CersDecisionStatus.NotAccepted &&
          !isInReview(d.status)
      ),
    },
  ];
  return groups.filter((g) => g.decisions.length > 0);
}

export function CersDecisionSummaryCell({
  summary,
  variant,
  state,
  tenantId,
  facilityName,
}: Props) {
  if (!summary) {
    const isStaffCa = variant === "staff" && state === "CA";
    if (isStaffCa) {
      const search = facilityName ? `?omnisearch=${encodeURIComponent(facilityName)}` : "";
      return (
        <MuiLink
          component={RouterLink}
          to={`/staff/cers/facilities${search}`}
          onClick={(e) => e.stopPropagation()}
        >
          <StatusChip label="Not matched" color="default" variant="outlined" />
        </MuiLink>
      );
    }
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  if (summary.moduleDecisions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No decisions yet
      </Typography>
    );
  }

  const groups = groupDecisions(summary.moduleDecisions);

  const revisionOkForVariant = variant === "staff" || (variant === "customer" && Boolean(tenantId));

  const revisionHref = (revisionReportId: string) =>
    variant === "customer"
      ? `/o/${tenantId}/chemicals/reports/${revisionReportId}`
      : `/staff/fulfillment/r/${revisionReportId}`;

  const popoverContent = (
    <Stack gap={1} sx={{ p: 0.5 }}>
      {groups.map((group) => (
        <Box key={group.key}>
          <StatusChip
            label={`${group.decisions.length} ${group.label}`}
            color={group.color}
            variant={group.chipVariant}
          />
          <Stack sx={{ pl: 2, pt: 0.5 }} gap={0.25}>
            {group.decisions.map((d) => {
              const dateStr = formatUtcDate(d.decisionDate);
              return (
                <Typography variant="body2" key={d.module}>
                  {prettyPrintEnumValue(d.module)}
                  {dateStr ? ` · ${dateStr}` : ""}
                  {d.revisionReportId && revisionOkForVariant && (
                    <>
                      {" · "}
                      <MuiLink
                        component={RouterLink}
                        to={revisionHref(d.revisionReportId)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        View revision
                      </MuiLink>
                    </>
                  )}
                </Typography>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );

  return (
    // The app theme defaults tooltips to disableInteractive (pointer-events:
    // none on the popper); this one holds a link, so it must stay interactive.
    <BigTooltip title={popoverContent} placement="bottom-start" disableInteractive={false}>
      {/* A real button so keyboard users can focus it and open with Enter/Space
          (native button click semantics drive the tooltip's click toggle). */}
      <ButtonBase
        aria-label="CERS module decisions"
        aria-haspopup="dialog"
        sx={{
          justifyContent: "flex-start",
          gap: 0.5,
          overflow: "hidden",
          // Fill the grid cell so a click anywhere in it opens the popover
          width: "100%",
          height: "100%",
        }}
      >
        {groups.map((group) => (
          <StatusChip
            key={group.key}
            label={group.decisions.length}
            aria-label={`${group.decisions.length} ${group.label.toLowerCase()}`}
            color={group.color}
            variant={group.chipVariant}
          />
        ))}
      </ButtonBase>
    </BigTooltip>
  );
}
